import React, { useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgNavigate } from "app/routes/useOrgNavigate";

import { Step, StepLabel, Stepper } from '@mui/material';
import Container from '@mui/material/Container';

import { useGetLocalStorage } from '@/operator/hooks/useGetLocalStorage';
import { useMultipleStepForm } from '@/operator/hooks/useMultiStepForm';
import type { ConnectToCRMAPIInput } from '@/operator/services/graphql/types/ConnectToCRMAPIInput';
import AllDone from '@/operator/components/modules/connections/AllDone';
import FormAddDetails from '@/operator/components/modules/connections/FormAddDetails';
import { FormAutoSelectService } from '@/operator/components/modules/connections/FormAutoSelectService';
import FormSelectData from '@/operator/components/modules/connections/FormSelectData';
import { PageStateContext, PageStateDispatchContext } from './formStateContext';
import {
    DEFAULT_INITIAL_PAGE_STATE,
    PageStateAction,
    PageStateActions,
    pageStateReducer,
} from './formStateReducer';
import { appRoutes } from '@/routes/constants';
import { useGetConnectionsQuery } from 'generated/graphql';
import { useAppSelector } from 'hooks/index';
import { selectPartnerData } from 'store/partner/partnerSelector';

const LOCAL_STORAGE_KEY = 'MULTISTEP_FORM';

export interface ConnectorFormProps {
    readonly connectionName: string;
}

export const ConnectorForm = ({
    connectionName,
}: ConnectorFormProps): JSX.Element => {
    const [shouldSkipSetup] = useState(false);
    const [hasBeenAutoCompletedStep1, setHasBeenAutoCompletedStep1] =
        useState(false);
    const [connectionData, setConnectionData] =
        useState<ConnectToCRMAPIInput>();

    const { instanceId } = useAppSelector(selectPartnerData);

    const { t } = useTranslation();

    const navigate = useOrgNavigate();

    const gotPageState = useGetLocalStorage(
        LOCAL_STORAGE_KEY,
        DEFAULT_INITIAL_PAGE_STATE,
        {
            listenForUpdates: true,
        },
    );

    const [pageState, pageStateDispatch] = useReducer(
        pageStateReducer,
        gotPageState,
    );

    const {
        data,
        loading,
        refetch: refreshOrganizationData,
    } = useGetConnectionsQuery({
        variables: {
            getConnectionInput: {
                where: {
                    organizationId: instanceId,
                },
            },
        },
    });

    const getConnectionExistingSetup = (systemToSetup: string) =>
        data.connections?.find(
            // biome-ignore lint/suspicious/noExplicitAny: <explanation>
            ({ system }) => system === systemToSetup,
        );

    const handleOnConnectionSelect = (systemToSetup: string) => {
        setHasBeenAutoCompletedStep1(true);

        if (hasBeenAutoCompletedStep1) {
            return;
        }
        const connectionExistingSetup =
            getConnectionExistingSetup(systemToSetup);

        if (connectionExistingSetup) {
            const existingConnectionCustomFields =
                connectionExistingSetup.metadata.custom_fields;

            const totalTimeSpentCustomField =
                existingConnectionCustomFields?.find(
                    ({ udm_field }: { udm_field?: string }) =>
                        udm_field === 'handle_total_time',
                );
            const totalTimeLastUpdatedField =
                existingConnectionCustomFields?.find(
                    ({ udm_field }: { udm_field?: string }) =>
                        udm_field === 'handle_time_last_update',
                );

            const customFieldMappingFound = Boolean(
                totalTimeSpentCustomField && totalTimeLastUpdatedField,
            );

            const respond: PageStateAction = {
                type: PageStateActions.SetFormData,
                attribute: 'connectionData',
                value: {
                    loadedFromExistingConnection: true,
                    custom_fields: !customFieldMappingFound
                        ? connectionExistingSetup.metadata.custom_fields
                        : [],
                    custom_field_found: customFieldMappingFound,
                },
            };

            pageStateDispatch(respond);
        }
    };

    const steps = useMemo(
        () => [
            {
                title: t('Forms.Connections.Step2.Title'),
                component: (
                    <FormAddDetails
                        sendConnectionData={(data) => setConnectionData(data)}
                        key="formStep2"
                        onContinue={() => next()}
                        onCancel={() =>
                            navigate(appRoutes.OPERATOR_ADMIN_CONNECTIONS)
                        }
                        refreshOrganizationData={refreshOrganizationData}
                    />
                ),
                skipOnSetupComplete: true,
            },
            {
                title: t('Forms.Connections.Step3.Title'),
                component: (
                    <FormSelectData
                        key="formStep3"
                        connectionData={connectionData}
                        onContinue={() => {
                            navigate(
                                `${appRoutes.OPERATOR_ADMIN_CONNECTIONS}?notification=success`,
                            );
                        }}
                        // Users within this step should never
                        // be able to go back to filling in auth data.
                        onBack={() => goTo(0)}
                        onCancel={() =>
                            navigate(appRoutes.OPERATOR_ADMIN_CONNECTIONS)
                        }
                        refreshOrganizationData={refreshOrganizationData}
                    />
                ),
            },

            {
                component: <AllDone key="formStep4" />,
            },
        ],
        [data?.connections, connectionName, connectionData],
    );

    const stepsTitles = steps.map(({ title }) => title).filter(Boolean);
    const stepsComponents = steps.map(({ component }) => component);
    const stepsIndexesToSkipOnSetupComplete = steps
        .map(({ skipOnSetupComplete }, index) =>
            skipOnSetupComplete ? index : undefined,
        )
        .filter((index) => index !== undefined);

    const { currentStepIndex, step, goTo, next } =
        useMultipleStepForm(stepsComponents);

    return (
        <PageStateContext.Provider value={pageState}>
            <PageStateDispatchContext.Provider value={pageStateDispatch}>
                <Container
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',

                        width: '984px',
                        borderRadius: '4px',
                        background: '#FFF',
                        padding: '48px !important',
                        boxShadow:
                            '0px 1px 3px 0px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.20) !important',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                        }}
                    >
                        <Stepper activeStep={currentStepIndex}>
                            {stepsTitles.map((title, index) => {
                                const shouldBeDisabled =
                                    (currentStepIndex === 0 && index !== 0) ||
                                    (currentStepIndex === 1 && index > 1) ||
                                    ((currentStepIndex === 2 ||
                                        currentStepIndex === 3) &&
                                        index !== 0) ||
                                    (shouldSkipSetup &&
                                        stepsIndexesToSkipOnSetupComplete.includes(
                                            index,
                                        ));
                                return (
                                    <Step
                                        key={title}
                                        disabled={shouldBeDisabled}
                                        onClick={() => {
                                            if (shouldBeDisabled) {
                                                return;
                                            }

                                            goTo(index);
                                        }}
                                        sx={{
                                            cursor: shouldBeDisabled
                                                ? 'not-allowed'
                                                : 'pointer',
                                        }}
                                    >
                                        <StepLabel>{title}</StepLabel>
                                    </Step>
                                );
                            })}
                        </Stepper>
                        {pageState.selectedConnector ? (
                            step
                        ) : (
                            <FormAutoSelectService
                                key="formStep1"
                                onContinue={handleOnConnectionSelect}
                                connections={data?.connections}
                                loading={loading}
                                connectionName={connectionName}
                                hasBeenAutoCompletedStep1={
                                    hasBeenAutoCompletedStep1
                                }
                                handleAutoCompleteStep1={() =>
                                    setHasBeenAutoCompletedStep1(true)
                                }
                            />
                        )}
                    </div>
                </Container>
            </PageStateDispatchContext.Provider>
        </PageStateContext.Provider>
    );
};
